import type { EntranceProtocol } from './types';

export const VALUE_COMPLIANCE_DEFAULT = false;

export type ValueComplianceUpstream = {
  id: string;
  name: string;
  status: string;
  protocol?: EntranceProtocol;
};

export type ValueComplianceUpstreamStatus = {
  state: 'disabled' | 'missing' | 'unavailable' | 'ready';
  text: string;
};

export function valueComplianceEnabledForProtocol(protocol: EntranceProtocol, enabled: unknown): boolean {
  return protocol === 'openai_http' && Boolean(enabled);
}

export function valueComplianceStatusText(enabled: unknown, policyVersion?: string): string {
  if (!enabled) return '关闭（不注入合规提示）';
  return policyVersion ? `启用（策略 ${policyVersion}）` : '启用';
}

export function valueComplianceUpstreamIdForProtocol(protocol: EntranceProtocol, upstreamId?: string): string | undefined {
  if (protocol !== 'openai_http') return undefined;
  const normalized = String(upstreamId || '').trim();
  return normalized && !/^0{24}$/.test(normalized) ? normalized : undefined;
}

export function valueComplianceModelForProtocol(protocol: EntranceProtocol, model?: string): string | undefined {
  if (protocol !== 'openai_http') return undefined;
  const normalized = String(model || '').trim();
  return normalized || undefined;
}

export function validateValueComplianceUpstream(protocol: EntranceProtocol, enabled: unknown, upstreamId?: string): string {
  if (!valueComplianceEnabledForProtocol(protocol, enabled)) return '';
  return valueComplianceUpstreamIdForProtocol(protocol, upstreamId) ? '' : '请选择合规专用上游服务';
}

export function validateValueComplianceConfiguration(
  protocol: EntranceProtocol,
  enabled: unknown,
  upstreamId?: string,
  model?: string
): string {
  const upstreamError = validateValueComplianceUpstream(protocol, enabled, upstreamId);
  if (upstreamError) return upstreamError;
  if (!valueComplianceEnabledForProtocol(protocol, enabled)) return '';
  return valueComplianceModelForProtocol(protocol, model) ? '' : '请输入合规专用模型';
}

export function valueComplianceUpstreamStatus(
  enabled: unknown,
  upstreamId: string | undefined,
  upstreams: ValueComplianceUpstream[],
  model?: string
): ValueComplianceUpstreamStatus {
  if (!enabled) return { state: 'disabled', text: '关闭时不生效' };
  const id = valueComplianceUpstreamIdForProtocol('openai_http', upstreamId);
  if (!id) return { state: 'missing', text: '未配置（受保护请求将返回 503）' };
  const upstream = upstreams.find((item) => item.id === id);
  if (!upstream) return { state: 'missing', text: '上游不存在（受保护请求将返回 503）' };
  if (upstream.status !== 'enabled' || (upstream.protocol && upstream.protocol !== 'openai_http')) {
    return { state: 'unavailable', text: `${upstream.name}（不可用，受保护请求将返回 503）` };
  }
  const normalizedModel = valueComplianceModelForProtocol('openai_http', model);
  if (!normalizedModel) return { state: 'missing', text: `${upstream.name} · 未配置合规模型（受保护请求将返回 503）` };
  return { state: 'ready', text: `${upstream.name} · 模型 ${normalizedModel}` };
}
